import type { ScreenHandle, ScreenKey } from "@/lib/nav/screens";

/**
 * 記録一覧の「商談記録｜議事録」タブ（PRODUCT_DEV-996 / PRODUCT_DEV-1277）
 *
 * 議事録は以前 /memo（「録音」）の中にあった。記録一覧の見出しの下のタブへ移した。
 *
 * どちらを開くかは次の順で決める:
 * 1. URL の `?list=minutes`（議事録の詳細・録音画面の「戻る」、古い `/memo?tab=minutes` の転送先）
 * 2. **← で戻ってきたなら、離れたときに開いていたタブ**（PRODUCT_DEV-1370。下の `readEntryListView`）
 * 3. **この端末で最後に見た記録の種類**（localStorage。端末ごとでよい、というユーザ判断）
 * 4. 既定は「商談記録」（記憶が消えた・読めないときもここ）
 *
 * 「最後に見た記録の種類」は次で書き換わる（2026-11-02 見直し。以前は「最後に開いたタブ」だけで、
 * 議事録の詳細 → 札から商談記録の詳細 → 記録一覧、で議事録が開いていた）:
 * - 議事録の詳細・会議録音を開いた → 議事録
 * - 商談記録の詳細・商談グループ・商談記録の作成を開いた → 商談記録
 * - 記録一覧でタブを切り替えた → そのタブ
 * 手帳・設定など記録でない画面では書き換えない。
 * Unizo は商談記録の道具で、議事録を使う会社はまだ少ないので、迷ったら商談記録に倒す。
 */
export type ListView = "meetings" | "minutes";

export const LIST_VIEW_PARAM = "list";
export const LIST_VIEW_STORAGE_KEY = "unizo.meetingsList.lastView";

const parse = (raw: string | null | undefined): ListView | null =>
  raw === "minutes" || raw === "meetings" ? raw : null;

/** 記憶を読む。読めない環境（プライベートモード・保存の拒否）では null。 */
export function readStoredListView(): ListView | null {
  try {
    return parse(window.localStorage.getItem(LIST_VIEW_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** 記憶する。書けなくてもタブの切り替えはできるので、失敗は黙って捨てる。 */
export function storeListView(view: ListView): void {
  try {
    window.localStorage.setItem(LIST_VIEW_STORAGE_KEY, view);
  } catch {
    // 覚えられないだけ（次回は既定の「商談記録」で開く）
  }
}

/**
 * ← で戻ってきたときのタブ（PRODUCT_DEV-1370）
 *
 * 「最後に見た記録の種類」だけで決めていたので、商談記録タブ → 議事録の札 → 議事録の詳細 → ← で
 * 議事録タブが開いた（議事録の詳細を開いた時点で記憶が「議事録」になる）。戻るを押したのに元のタブに
 * 戻らないのはおかしいので、**記録一覧の履歴の枠ごとに、離れたときのタブ**を覚える。
 * ← やブラウザの戻るは同じ枠へ戻るのでこれが使われ、タブバーやホームから開き直したときは
 * 新しい枠なので今までどおり「最後に見た記録の種類」になる（手帳の `diaryDateMemory` と同じ考え方）。
 * sessionStorage（そのタブを開いているあいだだけ）に置く。
 */
export const LIST_VIEW_BY_ENTRY_KEY = "unizo.meetingsList.viewByEntry";
/** 覚えておく枠の数。古いものから捨てる */
const ENTRY_LIMIT = 30;

const readEntryViews = (): Record<string, { view: ListView; at: number }> => {
  try {
    const raw = window.sessionStorage.getItem(LIST_VIEW_BY_ENTRY_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === "object"
      ? (parsed as Record<string, { view: ListView; at: number }>)
      : {};
  } catch {
    return {};
  }
};

/** この枠で最後に開いていたタブ。無ければ null */
export function readEntryListView(entryId: string | null): ListView | null {
  if (!entryId) return null;
  return parse(readEntryViews()[entryId]?.view);
}

export function rememberEntryListView(entryId: string | null, view: ListView): void {
  if (!entryId) return;
  try {
    const all = readEntryViews();
    all[entryId] = { view, at: Date.now() };
    const ids = Object.keys(all);
    if (ids.length > ENTRY_LIMIT) {
      ids
        .sort((a, b) => (all[a].at ?? 0) - (all[b].at ?? 0))
        .slice(0, ids.length - ENTRY_LIMIT)
        .forEach((id) => delete all[id]);
    }
    window.sessionStorage.setItem(LIST_VIEW_BY_ENTRY_KEY, JSON.stringify(all));
  } catch {
    // 覚えられないだけ（戻ったときは「最後に見た記録の種類」で開く）
  }
}

/** 開いたときのタブ。URL → この枠で開いていたタブ（← で戻ったとき）→ 端末の記憶 → 既定（商談記録）。 */
export function initialListView(
  searchParams: URLSearchParams,
  stored: ListView | null = readStoredListView(),
  entry: ListView | null = null,
): ListView {
  return parse(searchParams.get(LIST_VIEW_PARAM)) ?? entry ?? stored ?? "meetings";
}

/** その画面を開いたら、どちらの記録を見たことにするか。記録でない画面は null（記憶を変えない）。 */
const SCREEN_LIST_VIEW: Partial<Record<ScreenKey, ListView>> = {
  minutesDetail: "minutes",
  minutesRecording: "minutes",
  meetingDetail: "meetings",
  groupDetail: "meetings",
  recording: "meetings",
  speedRecording: "meetings",
};

export const listViewOfScreen = (key: ScreenKey | null | undefined): ListView | null =>
  (key && SCREEN_LIST_VIEW[key]) || null;

interface RouterLike {
  state: { matches: ReadonlyArray<{ route: { handle?: unknown } }> };
  subscribe: (fn: (state: RouterLike["state"]) => void) => () => void;
}

/** 画面を移るたびに「最後に見た記録の種類」を覚える。ルート定義の `handle.screen` を読む。 */
export function installListViewMemory(
  router: RouterLike,
  read: (h: unknown) => ScreenHandle | null,
): void {
  const record = (state: RouterLike["state"]) => {
    for (let i = state.matches.length - 1; i >= 0; i--) {
      const h = read(state.matches[i].route.handle);
      if (!h) continue;
      const view = listViewOfScreen(h.screen);
      if (view && view !== readStoredListView()) storeListView(view);
      return;
    }
  };
  record(router.state);
  router.subscribe(record);
}
